import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Form, Input, DatePicker, Typography, Space, Card, Row, Col, message, Modal } from 'antd';
import { SearchOutlined, CopyOutlined, LineChartOutlined, ApartmentOutlined } from '@ant-design/icons';
import API from '../services/api'; // 导入统一的API实例
import * as echarts from 'echarts'; // 导入ECharts
import html2canvas from 'html2canvas'; // 导入截图库
import dayjs from 'dayjs';
import usePermissionCheck from '../hooks/usePermissionCheck';
import PermissionDenied from '../components/PermissionDenied';

const { Title } = Typography;

const UserHuBeiXiaoQiangTrafficQuery = () => {
  const { hasPermission, isLoading: permissionLoading, userAccountType } = usePermissionCheck('canViewSmallWallLogs');
  
  const [ip, setIp] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [trafficData, setTrafficData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // 初始化默认时间范围（当天）
  useEffect(() => {
    const today = dayjs();
    setStartTime(today);
    setEndTime(today);
  }, []);

  // 验证日期范围
  const validateDateRange = (start, end) => {
    if (!start || !end) {
      return { isValid: false, message: '请选择开始时间和结束时间' };
    }

    if (start.isAfter(end)) {
      return { isValid: false, message: '开始时间不能晚于结束时间' };
    }

    const daysDiff = end.diff(start, 'day') + 1;
    if (daysDiff > 3) {
      return { isValid: false, message: '查询时间跨度不能超过3天' };
    }

    return { isValid: true, message: '' };
  };

  // 显示消息提示
  const showMessage = (content, type = 'success') => {
    message[type](content);
  };

  // 查询单个IP流量
  const handleIpTrafficQuery = async () => {
    // 验证IP地址
    if (!ip.trim()) {
      message.warning('请输入IP地址');
      return;
    }

    // 验证日期范围
    const validation = validateDateRange(startTime, endTime);
    if (!validation.isValid) {
      message.warning(validation.message);
      return;
    }

    setLoading(true);
    try {
      const response = await API.get('/api/user/get-flow-log-data', {
        params: {
          ip: ip.trim(),
          start_time: startTime ? startTime.format('YYYY-MM-DD') : '',
          end_time: endTime ? endTime.format('YYYY-MM-DD') : ''
        }
      });
      
      if (response.data.length === 0) {
        message.info('暂无数据');
        setTrafficData([]);
      } else {
        setTrafficData(response.data);
        message.success('查询成功');
      }
    } catch (error) {
      console.error('获取单个IP流量失败:', error);
      const errorMsg = error.response?.data?.message || '获取流量数据失败';
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
      setTrafficData([]);
    } finally {
      setLoading(false);
    }
  };

  // 截图复制功能
  const handleCopyScreenshot = async (elementId, label, buttonRef) => {
    try {
      const element = document.getElementById(elementId);
      if (!element) return;

      // 添加复制状态
      const button = buttonRef || document.querySelector(`[data-copy-target="${elementId}"]`);
      if (!button) return;
      
      const originalText = button.innerHTML;
      button.innerHTML = '<i class="fas fa-check"></i> 已复制';
      button.classList.add('copied');

      // 截图前隐藏复制按钮
      const copyButton = element.querySelector('button[data-copy-target]') || 
                         element.querySelector('.ant-card-extra button');
      if (copyButton) {
        copyButton.style.display = 'none';
      }

      // 添加版权信息
      const copyrightDiv = document.createElement('div');
      copyrightDiv.className = 'screenshot-copyright';
      copyrightDiv.innerHTML = '数据来源于DDoS流量监控系统';
      copyrightDiv.style.cssText = `
        position: absolute;
        bottom: 8px;
        left: 50%;
        transform: translateX(-50%);
        font-size: 10px;
        color: #6c757d;
        background: rgba(255, 255, 255, 0.9);
        padding: 4px 12px;
        border-radius: 4px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        border: 1px solid rgba(108, 117, 125, 0.2);
        z-index: 1000;
        text-align: center;
        white-space: nowrap;
      `;
      
      // 确保父元素有相对定位
      const originalPosition = element.style.position;
      element.style.position = 'relative';
      element.appendChild(copyrightDiv);

      // 截图
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2, // 提高分辨率
        useCORS: true,
        allowTaint: true,
        logging: false,
        onclone: (clonedDoc) => {
          // 在克隆的文档中也隐藏复制按钮
          const clonedElement = clonedDoc.getElementById(elementId);
          if (clonedElement) {
            const copyBtn = clonedElement.querySelector('.ant-card-extra button');
            if (copyBtn) {
              copyBtn.style.display = 'none';
            }
          }
        }
      });

      // 截图后清理：移除版权信息和恢复按钮显示
      element.removeChild(copyrightDiv);
      element.style.position = originalPosition;
      if (copyButton) {
        copyButton.style.display = '';
      }

      // 转换为Blob并复制到剪贴板
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'image/png': blob
            })
          ]);
          console.log('截图已成功复制到剪贴板');
          showMessage(`${label}已成功复制到剪贴板！`, 'success');
        } catch (error) {
          console.error('复制到剪贴板失败:', error);
          // 如果复制失败，下载图片
          const link = document.createElement('a');
          link.download = `${label}_统计.png`;
          link.href = canvas.toDataURL();
          link.click();
          
          // 更新按钮文字为下载提示
          button.innerHTML = '<i class="fas fa-download"></i> 已下载';
          showMessage(`${label}图片已下载到本地`, 'info');
        }
      });

      // 2秒后恢复按钮状态
      setTimeout(() => {
        if (button) {
          button.innerHTML = originalText;
          button.classList.remove('copied');
        }
      }, 2000);
    } catch (error) {
      console.error('截图失败:', error);
      showMessage('截图失败，请重试', 'error');
      
      // 确保清理状态
      const element = document.getElementById(elementId);
      if (element) {
        // 恢复复制按钮显示
        const copyButton = element.querySelector('button[data-copy-target]') || 
                           element.querySelector('.ant-card-extra button');
        if (copyButton) {
          copyButton.style.display = '';
        }
        // 清理可能残留的版权信息
        const existingCopyright = element.querySelector('.screenshot-copyright');
        if (existingCopyright) {
          element.removeChild(existingCopyright);
        }
      }
      
      // 如果有按钮引用，显示错误状态
      if (buttonRef) {
        const originalText = buttonRef.innerHTML;
        buttonRef.innerHTML = '<i class="fas fa-times"></i> 失败';
        buttonRef.style.color = '#ef4444';
        setTimeout(() => {
          buttonRef.innerHTML = originalText;
          buttonRef.style.color = '';
        }, 2000);
      }
    }
  };

  // 绘图函数定义
  const getUnit = (maxValue) => {
    if (maxValue >= 1e9) return { divisor: 1e9, unit: 'Gbps' };
    if (maxValue >= 1e6) return { divisor: 1e6, unit: 'Mbps' };
    if (maxValue >= 1e3) return { divisor: 1e3, unit: 'Kbps' };
    return { divisor: 1, unit: 'bps' };
  };

  const drawTrafficChart = useCallback((chart, dataArray) => {
    const labels = dataArray.map(data => `${data.hour < 10 ? `0${data.hour}` : data.hour}:${data.minute === 0 ? '00' : data.minute}`);

    const inSizeRaw = dataArray.map(data => (data.in_size * 8));
    const outSizeRaw = dataArray.map(data => (data.out_size * 8));
    const inDropSizeRaw = dataArray.map(data => (data.in_drop_size * 8));  // 增加拦截流量

    const maxValue = Math.max(...inSizeRaw, ...outSizeRaw, ...inDropSizeRaw);
    const { divisor, unit } = getUnit(maxValue);

    const inSize = inSizeRaw.map(size => (size / divisor).toFixed(2));
    const outSize = outSizeRaw.map(size => (size / divisor).toFixed(2));
    const inDropSize = inDropSizeRaw.map(size => (size / divisor).toFixed(2));

    // 计算所有流量数据的最大最小值
    const inSizeNumbers = inSize.map(Number);
    const outSizeNumbers = outSize.map(Number);
    const inDropSizeNumbers = inDropSize.map(Number);

    const maxInFlow = Math.max(...inSizeNumbers).toFixed(2);
    const minInFlow = Math.min(...inSizeNumbers).toFixed(2);
    const maxOutFlow = Math.max(...outSizeNumbers).toFixed(2);
    const minOutFlow = Math.min(...outSizeNumbers).toFixed(2);
    const maxDropFlow = Math.max(...inDropSizeNumbers).toFixed(2);
    const minDropFlow = Math.min(...inDropSizeNumbers).toFixed(2);

    // 更新图表信息显示
    const trafficStatsElement = document.getElementById('trafficStats');
    if (trafficStatsElement) {
      trafficStatsElement.innerHTML = `
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #52c41a; font-size: 24px; font-weight: 600;">${maxInFlow} ${unit}</div>
          <div style="color: #666; font-size: 14px;">最大输入流量</div>
        </div>
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #1890ff; font-size: 24px; font-weight: 600;">${minInFlow} ${unit}</div>
          <div style="color: #666; font-size: 14px;">最小输入流量</div>
        </div>
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #52c41a; font-size: 24px; font-weight: 600;">${maxOutFlow} ${unit}</div>
          <div style="color: #666; font-size: 14px;">最大输出流量</div>
        </div>
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #1890ff; font-size: 24px; font-weight: 600;">${minOutFlow} ${unit}</div>
          <div style="color: #666; font-size: 14px;">最小输出流量</div>
        </div>
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #ff4d4f; font-size: 24px; font-weight: 600;">${maxDropFlow} ${unit}</div>
          <div style="color: #666; font-size: 14px;">最大拦截流量</div>
        </div>
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #ff4d4f; font-size: 24px; font-weight: 600;">${minDropFlow} ${unit}</div>
          <div style="color: #666; font-size: 14px;">最小拦截流量</div>
        </div>
      `;
    }

    const trafficOption = {
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '5%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#d9d9d9',
        textStyle: {
          color: '#333'
        },
        formatter: (params) => {
          const { time, hour, minute } = dataArray[params[0].dataIndex];
          const formattedTime = `${time} ${hour < 10 ? `0${hour}` : hour}:${minute === 0 ? '00' : minute}`;
          let content = `时间: ${formattedTime}<br/>`;
          params.forEach(item => {
            content += `${item.seriesName}: ${item.value} ${unit}<br/>`;
          });
          return content;
        }
      },
      legend: {
        data: [`输入流量 (${unit})`, `输出流量 (${unit})`, `输入拦截流量 (${unit})`],
        bottom: '5%',
        textStyle: {
          fontSize: 12,
          color: '#666'
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLine: {
          lineStyle: {
            color: '#d9d9d9'
          }
        },
        axisLabel: {
          color: '#666'
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: '#d9d9d9'
          }
        },
        axisLabel: {
          formatter: `{value} ${unit}`,
          color: '#666'
        }
      },
      series: [
        {
          name: `输入流量 (${unit})`,
          type: 'line',
          data: inSize,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#1890ff'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(24, 144, 255, 0.3)'
              }, {
                offset: 1, color: 'rgba(24, 144, 255, 0.1)'
              }],
            },
          },
        },
        {
          name: `输出流量 (${unit})`,
          type: 'line',
          data: outSize,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#52c41a'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(82, 196, 26, 0.3)'
              }, {
                offset: 1, color: 'rgba(82, 196, 26, 0.1)'
              }],
            },
          },
        },
        {
          name: `输入拦截流量 (${unit})`,
          type: 'line',
          data: inDropSize,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#ff4d4f'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(255, 77, 79, 0.3)'
              }, {
                offset: 1, color: 'rgba(255, 77, 79, 0.1)'
              }],
            },
          },
        }
      ]
    };
    chart.setOption(trafficOption);
  }, []);

  const drawConnectionChart = useCallback((chart, dataArray) => {
    const labels = dataArray.map(data => `${data.hour < 10 ? `0${data.hour}` : data.hour}:${data.minute === 0 ? '00' : data.minute}`);

    const maxInCount = dataArray.map(data => data.average_connect_in_count);
    const maxOutCount = dataArray.map(data => data.average_connect_out_count);

    // 计算连接数的最大最小值
    const maxInConnections = Math.max(...maxInCount);
    const minInConnections = Math.min(...maxInCount);
    const maxOutConnections = Math.max(...maxOutCount);
    const minOutConnections = Math.min(...maxOutCount);

    // 更新连接统计信息显示
    const connectionStatsElement = document.getElementById('connectionStats');
    if (connectionStatsElement) {
      connectionStatsElement.innerHTML = `
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #722ed1; font-size: 24px; font-weight: 600;">${maxInConnections} 个</div>
          <div style="color: #666; font-size: 14px;">最大输入新建连接数</div>
        </div>
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #13c2c2; font-size: 24px; font-weight: 600;">${minInConnections} 个</div>
          <div style="color: #666; font-size: 14px;">最小输入新建连接数</div>
        </div>
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #722ed1; font-size: 24px; font-weight: 600;">${maxOutConnections} 个</div>
          <div style="color: #666; font-size: 14px;">最大输出新建连接数</div>
        </div>
        <div style="padding: 16px; background: #f0f2f5; border-radius: 6px; text-align: center;">
          <div style="color: #13c2c2; font-size: 24px; font-weight: 600;">${minOutConnections} 个</div>
          <div style="color: #666; font-size: 14px;">最小输出新建连接数</div>
        </div>
      `;
    }

    const connectionOption = {
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '5%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#d9d9d9',
        textStyle: {
          color: '#333'
        },
        formatter: (params) => {
          const { time, hour, minute } = dataArray[params[0].dataIndex];
          const formattedTime = `${time} ${hour < 10 ? `0${hour}` : hour}:${minute === 0 ? '00' : minute}`;
          let content = `时间: ${formattedTime}<br/>`;
          params.forEach(item => {
            content += `${item.seriesName}: ${item.value} 个<br/>`;
          });
          return content;
        }
      },
      legend: {
        data: ['最大输入新建连接', '最大输出新建连接'],
        bottom: '5%',
        textStyle: {
          fontSize: 12,
          color: '#666'
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLine: {
          lineStyle: {
            color: '#d9d9d9'
          }
        },
        axisLabel: {
          color: '#666'
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: '#d9d9d9'
          }
        },
        axisLabel: {
          color: '#666'
        }
      },
      series: [
        {
          name: '最大输入新建连接',
          type: 'line',
          data: maxInCount,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#722ed1'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(114, 46, 209, 0.3)'
              }, {
                offset: 1, color: 'rgba(114, 46, 209, 0.1)'
              }],
            },
          },
        },
        {
          name: '最大输出新建连接',
          type: 'line',
          data: maxOutCount,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#fa8c16'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(250, 140, 22, 0.3)'
              }, {
                offset: 1, color: 'rgba(250, 140, 22, 0.1)'
              }],
            },
          },
        }
      ]
    };
    chart.setOption(connectionOption);
  }, []);

  useEffect(() => {
    if (trafficData.length > 0) {
      const trafficChart = echarts.init(document.getElementById('trafficChart'));
      const connectionChart = echarts.init(document.getElementById('connectionChart'));

      drawTrafficChart(trafficChart, trafficData);
      drawConnectionChart(connectionChart, trafficData);

      // 添加resize监听，确保图表大小自适应容器
      window.addEventListener('resize', () => {
        trafficChart.resize();
        connectionChart.resize();
      });

      // 销毁图表实例以防止内存泄漏
      return () => {
        trafficChart.dispose();
        connectionChart.dispose();
        window.removeEventListener('resize', () => {
          trafficChart.resize();
          connectionChart.resize();
        });
      };
    }
  }, [trafficData, drawTrafficChart, drawConnectionChart]);

  // 权限检查 - 移到所有hooks之后
  if (!hasPermission) {
    return (
      <PermissionDenied 
        isLoading={permissionLoading}
        permissionName="小墙流量"
        userAccountType={userAccountType}
      />
    );
  }

  return (
    <div style={{
      maxWidth: '1400px',
      padding: '30px 24px',
      margin: '0 auto'
    }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 标题和说明 */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 20, color: '#1890ff' }}>
            流量查询(小墙)
          </Title>
          
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Alert
              message="此功能仅限云主机和托管服务器的IP使用，支持自定义日期查询，时间跨度最多3天"
              type="info"
              showIcon
              style={{ 
                maxWidth: 600, 
                textAlign: 'center',
                border: '1px solid #91d5ff',
                borderRadius: 6
              }}
            />
          </div>
        </div>

        {/* 查询表单 */}
        <Row justify="center">
          <Col xs={24} md={18} lg={16}>
            <Card>
              <Form layout="vertical">
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="开始时间">
                      <DatePicker
                        value={startTime}
                        onChange={setStartTime}
                        style={{ width: '100%' }}
                        placeholder="选择开始时间"
                        disabled={loading}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="结束时间">
                      <DatePicker
                        value={endTime}
                        onChange={setEndTime}
                        style={{ width: '100%' }}
                        placeholder="选择结束时间"
                        disabled={loading}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="IP地址">
                      <Input
                        placeholder="请输入查询IP地址"
                        value={ip}
                        onChange={(e) => setIp(e.target.value)}
                        onPressEnter={handleIpTrafficQuery}
                        disabled={loading}
                        addonAfter={
                          <Button 
                            type="primary"
                            onClick={handleIpTrafficQuery}
                            icon={<SearchOutlined />}
                            loading={loading}
                            disabled={loading || !ip.trim()}
                            size="small"
                            style={{ border: 'none' }}
                          >
                            查询
                          </Button>
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>
        </Row>

        {/* 图表区域 */}
        {trafficData.length > 0 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* 流量统计图表 */}
                <Card
                  title={
                    <span>
                      <LineChartOutlined style={{ marginRight: 8 }} />
                      流量统计图表
                    </span>
                  }
                  extra={
                    <Button 
                      type="default"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={(e) => handleCopyScreenshot('trafficCard', '流量统计图表', e.target)}
                    >
                      复制图表
                    </Button>
                  }
                  id="trafficCard"
                >
                  <div id="trafficStats" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: 16, 
                    marginBottom: 16 
                  }}></div>
                  <div id="trafficChart" style={{ height: '400px', width: '100%' }}></div>
                </Card>
                
                {/* 连接统计图表 */}
                <Card
                  title={
                    <span>
                      <ApartmentOutlined style={{ marginRight: 8 }} />
                      连接统计图表
                    </span>
                  }
                  extra={
                    <Button 
                      type="default"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={(e) => handleCopyScreenshot('connectionCard', '连接统计图表', e.target)}
                    >
                      复制图表
                    </Button>
                  }
                  id="connectionCard"
                >
                  <div id="connectionStats" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: 16, 
                    marginBottom: 16 
                  }}></div>
                  <div id="connectionChart" style={{ height: '400px', width: '100%' }}></div>
                </Card>
            </Space>
        )}
      </Space>
      
      {/* 错误提示弹窗 */}
      <Modal 
        title="操作失败" 
        open={showErrorModal} 
        onCancel={() => setShowErrorModal(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setShowErrorModal(false)}>
            确定
          </Button>
        ]}
      >
        {errorMessage}
      </Modal>
    </div>
  );
};

export default UserHuBeiXiaoQiangTrafficQuery;
